import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShieldCheck } from "lucide-react";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const sections = [
  {
    title: "1. Introduction",
    body: [
      "The Oil and Gas Free Zones Authority, OGFZA, respects the privacy of all visitors, investors, licensees, applicants, contractors, partners and stakeholders who use its website, portal and digital services.",
      "This Privacy Policy explains how OGFZA collects, uses, stores, protects and manages personal information submitted through its website, online forms, portal services, email channels and other digital platforms.",
      "By using this website or any related digital service, you agree to the terms of this Privacy Policy.",
    ],
  },
  {
    title: "2. Information We Collect",
    body: [
      "OGFZA may collect personal information when you visit the website, complete an online form, apply for a licence, create a portal account, submit documents, send an enquiry, register for a service, or communicate with the Authority.",
      "The information may include: Name; Email address; Phone number; Organisation or company name; Job title or designation; Contact address; Business registration details; Tax identification details, where required; Licence application information; Documents uploaded through the website or portal; Payment reference or transaction information, where applicable; Internet Protocol address; Browser type; Device information; Website usage data; Any other information submitted voluntarily through the website or portal.",
    ],
  },
  {
    title: "3. How We Collect Information",
    body: [
      "OGFZA may collect information through: Contact forms; Licence registration and application forms; Online portal services; Email communication; Complaint and enquiry forms; Document uploads; Event or stakeholder registration forms; Cookies and similar website technologies; Website analytics tools; Server logs; Official digital communication channels.",
    ],
  },
  {
    title: "4. Purpose of Collecting Information",
    body: [
      "OGFZA may collect and process personal information for the following purposes: To provide official information about OGFZA services; To process licence applications, renewals and related approvals; To manage investor, licensee and stakeholder records; To verify submitted documents and business information; To respond to enquiries, complaints and service requests; To improve website, portal and digital service delivery; To communicate official notices, updates and service information; To support regulatory, administrative and statutory functions; To prevent fraud, misuse, cyber threats and unauthorised access; To comply with legal, regulatory and public-sector obligations.",
    ],
  },
  {
    title: "5. Lawful Basis for Processing",
    body: [
      "OGFZA processes personal information based on one or more lawful grounds, including: Consent, where information is voluntarily submitted; Legal obligation, where processing is required by law or regulation; Public interest and official authority, where processing supports OGFZA’s statutory mandate; Contractual necessity, where processing is required to provide a requested service; Legitimate interest, where processing supports secure, efficient and accountable service delivery.",
    ],
  },
  {
    title: "6. Use of Cookies",
    body: [
      "The OGFZA website may use cookies and similar technologies to improve website performance, analyse traffic, remember user preferences and strengthen security.",
      "Users may disable cookies through their browser settings. Some features of the website may not function properly where cookies are disabled.",
    ],
  },
  {
    title: "7. Sharing of Personal Information",
    body: [
      "OGFZA does not sell personal information.",
      "OGFZA may share personal information only where necessary and lawful, including with: Authorised OGFZA departments and officers; Relevant government ministries, departments and agencies; Regulatory, security or law enforcement agencies where required by law; Technology service providers supporting hosting, portal services, email, cybersecurity and data storage; Professional advisers, auditors or consultants under confidentiality obligations; Any other party where disclosure is required by law, court order or lawful public authority request.",
    ],
  },
  {
    title: "8. Data Security",
    body: [
      "OGFZA applies reasonable administrative, technical and organisational measures to protect personal information from unauthorised access, alteration, disclosure, loss, misuse or destruction.",
      "These measures may include access control, secure hosting, password protection, encryption where appropriate, system monitoring, malware protection, staff confidentiality obligations and periodic security reviews.",
      "Users are responsible for keeping their login details confidential and must not share their passwords or account access with unauthorised persons.",
    ],
  },
  {
    title: "9. Data Retention",
    body: [
      "OGFZA will retain personal information only for as long as necessary to fulfil the purpose for which it was collected.",
      "Information may also be retained where required for legal compliance, regulatory reporting, official records, audit purposes, dispute resolution, security management and enforcement of applicable policies.",
      "Where personal information is no longer required, OGFZA will take reasonable steps to delete, anonymise or archive it in line with applicable records-management requirements.",
    ],
  },
  {
    title: "10. Your Data Protection Rights",
    body: [
      "Subject to applicable law, you may have the right to: Request access to your personal information; Request correction of inaccurate or incomplete information; Request deletion of your personal information where legally permitted; Withdraw consent where processing is based on consent; Object to certain forms of processing; Request restriction of processing; Request information on how your data is used; Lodge a complaint with the appropriate data protection authority.",
      "OGFZA may require identity verification before processing any data-protection request.",
    ],
  },
  {
    title: "11. Children’s Privacy",
    body: [
      "The OGFZA website and portal are not designed to collect personal information from children without lawful consent or proper authorisation.",
      "Where OGFZA becomes aware that personal information of a child has been submitted without appropriate authority, reasonable steps will be taken to delete or restrict the information, unless retention is required by law.",
    ],
  },
  {
    title: "12. Third-Party Links",
    body: [
      "The OGFZA website may contain links to third-party websites, platforms or services.",
      "OGFZA is not responsible for the privacy practices, content, security or policies of third-party websites. Users should review the privacy policies of external websites before submitting personal information.",
    ],
  },
  {
    title: "13. International Data Transfer",
    body: [
      "Where personal information is transferred outside Nigeria through hosting, cloud services, email systems, analytics tools or technology providers, OGFZA will take reasonable steps to ensure that such transfers comply with applicable data-protection requirements and that adequate safeguards are in place.",
    ],
  },
  {
    title: "14. Data Breach Management",
    body: [
      "Where a data breach affects personal information, OGFZA will take reasonable steps to contain the incident, assess the risk, investigate the cause, notify relevant authorities where required, and communicate with affected individuals where necessary under applicable law.",
    ],
  },
  {
    title: "15. User Responsibility",
    body: [
      "Users of the OGFZA website, portal and digital services are responsible for ensuring that the information they submit is accurate, complete and lawful.",
      "Users must not submit false information, impersonate another person, upload harmful files, attempt unauthorised access, interfere with website security, or misuse any OGFZA digital service.",
    ],
  },
  {
    title: "16. Updates to This Privacy Policy",
    body: [
      "OGFZA may update this Privacy Policy from time to time to reflect changes in law, technology, website features, portal services or internal data-management practices.",
      "Any updated version will be published on this website with a revised effective date.",
    ],
  },
  {
    title: "17. Contact Information",
    body: [
      "For questions, requests, comments or complaints relating to this Privacy Policy or the handling of personal information, please contact:",
      "Data Protection Officer",
      "Oil and Gas Free Zones Authority",
      "Head Office: Marble House, Federal Ocean Terminal, PMB 5474, Onne Port Complex, Rivers State, Nigeria",
      "Website: https://ogfza.gov.ng/",
      "Email: privacy@ogfza.gov.ng",
      "Telephone: +2348090243111",
      "General Contact Email: contact@ogfza.gov.ng",
    ],
  },
  {
    title: "18. Acceptance",
    body: [
      "By using the OGFZA website, portal or digital services, you acknowledge that you have read and understood this Privacy Policy and agree to the collection, use and management of your information as described in this policy.",
    ],
  },
];

const PrivacyPolicy = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", { opacity: 0, y: 30, duration: 0.8 });
      gsap.from(".hero-title", { opacity: 0, y: 80, duration: 1.1 });
      gsap.from(".hero-text", { opacity: 0, y: 40, duration: 0.9, delay: 0.25 });
      gsap.from(".legal-card", { opacity: 0, y: 45, duration: 0.8, stagger: 0.06 });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-slate-50">
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img src={HERO_IMAGE} alt="Privacy Policy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />
          <div className="absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Legal Information
              </span>
            </div>

            <h1 className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]">
              Privacy Policy
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Oil and Gas Free Zones Authority — Website: https://ogfza.gov.ng/
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span className="text-[#ff6600]">Privacy Policy</span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-white rounded-[34px] p-6 md:p-10 shadow-[0_18px_45px_rgba(0,0,0,0.10)] border border-slate-100 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center mb-5">
            <ShieldCheck size={28} />
          </div>
          <p className="text-sm font-bold text-[#ff6600]">Effective Date: [Insert Date]</p>
          <h2 className="text-3xl font-black text-[#06183f] mt-2">Oil and Gas Free Zones Authority</h2>
        </div>

        <div className="space-y-5">
          {sections.map((section) => (
            <article key={section.title} className="legal-card bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm">
              <h3 className="text-2xl font-black text-[#06183f] mb-4">{section.title}</h3>
              <div className="space-y-4">
                {section.body.map((paragraph, index) => (
                  <p key={index} className="text-slate-700 leading-[1.9]">
                    {paragraph}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;